import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import Upload, { ProfileUploadFile } from '@common/upload';
import Icon from '@common/assets';
import { profileImageBeforeUpload } from '@common/upload/beforeUpload';
import ImageCropModal from '@components/profile/profileOverlay/overlays/basicInfo/imageCropModal';
import EmptyProfileImage from '@components/profile/profileOverlay/overlays/basicInfo/EmptyProfileImage';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useProfilesFileImage, useUploadProfileFile } from '@repository/profiles/useProfile';
import Colors from '@domain/constant/colors';
import { queryClient } from '@utils/queryClient';
import classnames from 'classnames/bind';
import styles from './profileImageChange.module.scss';
const cx = classnames.bind(styles);

const ProfileImage = () => {
  const { data: userInfo } = useUserInfo();
  const [profileImageTooltip, setProfileImageTooltip] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [imgName, setImgName] = useState('');
  const [cropModalVisible, setCropModalVisible] = useState(false);

  const { mutateAsync: saveProfileImageAsync } = useProfilesFileImage(() => {
    // 매칭 현황에서 이미지 변경 시 Header에 있는 내 프로필 이미지 업데이트를 위해 userInfo refetch
    const accessToken = Cookies.get('accessToken');
    const useUserInfoKey = [accessToken, 'userInfo'];
    queryClient.invalidateQueries(useUserInfoKey);
  });
  const { mutateAsync: uploadProfileFile } = useUploadProfileFile();

  useEffect(() => {
    if (!userInfo) return;

    setProfileImageUrl(userInfo.profileImageUrl);
    setImgSrc(userInfo.profileImageUrl);
  }, [userInfo]);

  const handleProfileImageBtn = () => {
    setProfileImageTooltip(!profileImageTooltip);
  };

  const onBlur = () => {
    setProfileImageTooltip(false);
  };

  const handleProfileImageDelete = () => {
    setImgSrc(null);
    setProfileImageUrl(null);
    setProfileImageTooltip(false);
    saveProfileImageAsync(null);
  };

  const handleUpload = async (uploadFile: ProfileUploadFile) => {
    setProfileImageTooltip(false);
    if (!uploadFile) return;
    if (uploadFile.fileUrl) {
      setImgSrc(uploadFile.fileUrl);
      setImgName(uploadFile.fileName);
      setCropModalVisible(true);
    }
  };

  const onUploadFiles = async (files: File[]) => {
    const uploadedFiles = files.map(async (file) => {
      const response = await uploadProfileFile(file);
      await saveProfileImageAsync(response.fileUid);
      return response;
    });

    return Promise.all(uploadedFiles);
  };

  const setProfileImage = async (canvas: HTMLCanvasElement) => {
    const file = convertCanvasToFile(canvas.toDataURL());
    const uploadFiles = await onUploadFiles([file]);
    if (!uploadFiles.length) return;
    const uploadFile = uploadFiles[0];
    setProfileImageUrl(uploadFile.fileUrl);
  };

  const convertCanvasToFile = (dataUrl: string) => {
    const blobBin = atob(dataUrl.split(',')[1]); // base64 데이터 디코딩
    const array = [];
    for (let i = 0; i < blobBin.length; i++) {
      array.push(blobBin.charCodeAt(i));
    }
    return new File([new Blob([new Uint8Array(array)], { type: 'image/png' })], imgName);
  };

  if (!imgSrc) return <EmptyProfileImage handleUpload={handleUpload} />;

  return (
    <div className={cx('profileImageArea')}>
      <div
        className={cx('profileImage')}
        style={{ backgroundImage: `url(${profileImageUrl})` }}
        tabIndex={0}
        onClick={handleProfileImageBtn}
        onBlur={onBlur}
      >
        <a className={cx('btnProfile')} role="button" />
        <div className={cx('tooltipArea', { visible: profileImageTooltip })}>
          <label className={cx('btnTooltip')}>
            프로필 수정
            <Upload
              type={'PROFILE'}
              name={'profile'}
              labelClassName={cx('file')}
              value={[]}
              onChange={(uploadFile) => handleUpload(uploadFile[0])}
              authYn={false}
              accepts={['.jpg', '.png']}
              beforeUpload={profileImageBeforeUpload}
            />
          </label>
          <a className={cx('btnTooltip')} role="button" onClick={handleProfileImageDelete}>
            프로필 삭제
          </a>
        </div>
        <span className={cx('iconCamera')}>
          <Icon name="cameraLight" width={32} height={32} fill={Colors.C_WHITE} />
        </span>
      </div>

      <ImageCropModal
        visible={cropModalVisible}
        imgSrc={imgSrc}
        onClick={setProfileImage}
        onClose={() => setCropModalVisible(false)}
      />
    </div>
  );
};

export default ProfileImage;
